// Blocks.js
import React, { useState, useEffect, useRef } from 'react';
import './Blocks.css';

// Import block images - update paths according to your folder structure
import block1 from '../Assets/block1.jpeg';
import block2 from '../Assets/block2.jpeg';
import block3 from '../Assets/block3.jpeg';
import block4 from '../Assets/block4.jpeg';

const Blocks = ({ language = 'en', setCurrentPage }) => {
  const [selectedProduct, setSelectedProduct] = useState(null); // Changed from 0 to null
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('specifications');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const productsRef = useRef(null);

  // SEO-optimized product data
  const products = [
    {
      id: 1,
      name: 'Quartzite Borghese',
      image: block1,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Premium Quartzite Borghese Natural Stone Blocks | Raisen Mine India',
      metaDescription: 'Quartzite Borghese blocks from Raisen mine, India. Premium beige quartzite with elegant veining patterns. Direct from quarry, available for worldwide shipping from Xiamen port.',
      keywords: ['quartzite borghese', 'beige quartzite blocks', 'natural stone blocks', 'raisen quartzite', 'indian quartzite'],
      description: 'Quartzite Borghese represents the pinnacle of natural stone elegance. Extracted from our exclusive Raisen mine in India, these premium blocks showcase a sophisticated beige base with subtle gray veining that creates a timeless aesthetic.',
      features: [
        'Exceptional durability with 98.82 MPa compressive strength',
        'Natural beige coloration with unique veining patterns',
        'Ideal for luxury architectural projects and monuments',
        'Superior polish retention and weather resistance',
        'Consistent quality from single-source quarry'
      ],
      applications: [
        'Luxury hotel lobbies and commercial facades',
        'High-end residential flooring and wall cladding',
        'Monumental sculptures and artistic installations',
        'Premium kitchen countertops and bathroom vanities',
        'Exterior architectural elements and landscaping'
      ],
      technicalSpecs: {
        'Compressive Strength': '98.82 MPa',
        'Water Absorption': '< 1.6%',
        'Density': '2471 kg/m³',
        'SiO₂ Content': '> 96%',
        'Block Dimensions': 'Up to 3.0m x 2.0m x 2.0m',
        'Color Consistency': 'Excellent'
      }
    },
    {
      id: 2,
      name: 'Norwegian Wood Grain',
      image: block2,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Norwegian Wood Grain Quartzite Blocks | Natural Wood Pattern Stone',
      metaDescription: 'Norwegian Wood Grain quartzite blocks featuring stunning natural wood-like patterns. Premium quality from Raisen mine, perfect for luxury interiors. Ships worldwide from Xiamen.',
      keywords: ['norwegian wood grain', 'wood grain quartzite', 'wood pattern stone', 'natural wood texture quartzite', 'wood look stone blocks'],
      description: 'Norwegian Wood Grain quartzite captures the organic beauty of ancient forests in eternal stone. This remarkable natural quartzite features distinctive linear patterns that mirror the finest wood grains, offering the warmth of timber with the durability of stone.',
      features: [
        'Authentic wood grain patterns formed over millions of years',
        'Exceptional hardness surpassing granite specifications',
        'Zero maintenance compared to natural wood',
        'Frost and heat resistant for all climates',
        'Each block unique with natural variation'
      ],
      applications: [
        'Contemporary office buildings and corporate headquarters',
        'Luxury yacht and superyacht interiors',
        'High-end retail stores and boutique hotels',
        'Modern residential feature walls and flooring',
        'Spa and wellness center designs'
      ],
      technicalSpecs: {
        'Compressive Strength': '102.5 MPa',
        'Water Absorption': '< 1.4%',
        'Density': '2485 kg/m³',
        'Flexural Strength': '14.8 MPa',
        'Block Dimensions': 'Up to 2.8m x 1.8m x 1.8m',
        'Grain Direction': 'Consistent linear'
      }
    },
    {
      id: 3,
      name: 'Brazilian Wood Grain',
      image: block3,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Brazilian Wood Grain Quartzite | Exotic Wood Pattern Natural Stone',
      metaDescription: 'Brazilian Wood Grain quartzite blocks with exotic natural patterns. Premium quality stone with wood-like appearance from Raisen quarry. Global shipping from Xiamen port.',
      keywords: ['brazilian wood grain', 'exotic quartzite', 'wood texture stone', 'brazilian pattern quartzite', 'tropical wood stone'],
      description: 'Brazilian Wood Grain quartzite embodies the exotic beauty of tropical hardwoods frozen in geological time. This extraordinary stone features rich, warm tones with dramatic grain patterns reminiscent of the finest Brazilian timber species.',
      features: [
        'Rich golden-brown base with intricate grain patterns',
        'Superior strength exceeding industry standards',
        'Natural UV resistance maintaining color integrity',
        'Acid-resistant properties for versatile applications',
        'Sustainable alternative to endangered hardwoods'
      ],
      applications: [
        'Luxury resort and hospitality projects',
        'Museum and gallery feature installations',
        'Premium residential estates and penthouses',
        'Restaurant and bar statement pieces',
        'Outdoor pavilions and poolside features'
      ],
      technicalSpecs: {
        'Compressive Strength': '105.3 MPa',
        'Water Absorption': '< 1.3%',
        'Density': '2492 kg/m³',
        'Abrasion Resistance': 'Excellent',
        'Block Dimensions': 'Up to 3.2m x 2.1m x 2.0m',
        'Pattern Variation': 'Medium to High'
      }
    },
    {
      id: 4,
      name: 'Teak Wood Quartzite',
      image: block4,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Teak Wood Quartzite Blocks | Premium Teak Pattern Natural Stone',
      metaDescription: 'Teak Wood Quartzite blocks mimicking genuine teak wood patterns. Luxury natural stone from Raisen mine India. Perfect for high-end projects. Ships from Xiamen port.',
      keywords: ['teak wood quartzite', 'teak pattern stone', 'teak look quartzite', 'wood grain natural stone', 'teak texture blocks'],
      description: 'Teak Wood Quartzite masterfully replicates the prestigious appearance of teak timber in an eternal stone format. This premium quartzite features the characteristic straight grain and golden-brown hues of teak, offering unmatched durability with timeless elegance.',
      features: [
        'Authentic teak wood appearance in natural stone',
        'Exceptional weathering properties for exterior use',
        'Zero risk of termites, rot, or degradation',
        'Maintains beauty without oiling or treatment',
        'Environmentally sustainable teak alternative'
      ],
      applications: [
        'Luxury marine and coastal architecture',
        'Five-star hotel lobbies and suites',
        'High-end residential developments',
        'Corporate boardrooms and executive offices',
        'Outdoor decking and facade cladding'
      ],
      technicalSpecs: {
        'Compressive Strength': '99.7 MPa',
        'Water Absorption': '< 1.5%',
        'Density': '2478 kg/m³',
        'Thermal Stability': 'Excellent',
        'Block Dimensions': 'Up to 2.9m x 1.9m x 1.9m',
        'Surface Finish': 'Multiple options available'
      }
    }
  ];

  const translations = {
    en: {
      pageTitle: 'BLOCKS',
      subtitle: 'Premium Quartzite Blocks',
      description: 'Direct from our Raisen quarry, we offer the finest selection of natural quartzite blocks with stunning wood grain patterns. Each block is carefully extracted and inspected to meet the highest international standards for luxury architectural projects.',
      overview: 'Product Overview',
      specifications: 'Technical Specifications',
      applications: 'Applications',
      features: 'Key Features',
      mine: 'Mine Location',
      shipping: 'Shipping Port',
      availability: 'Availability',
      inStock: 'In Stock - Ready to Ship',
      contactForPrice: 'Contact for Pricing',
      requestQuote: 'Request Quote',
      viewDetails: 'View Full Details',
      whyChoose: 'Why Choose Our Quartzite Blocks?',
      directSource: 'Direct from Source',
      directSourceDesc: 'Eliminate middlemen with direct quarry access, ensuring best prices and consistent quality.',
      qualityAssurance: 'Quality Assurance',
      qualityDesc: 'Every block inspected and graded to meet international standards for premium projects.',
      globalShipping: 'Global Shipping',
      globalDesc: 'Efficient logistics from Xiamen port to destinations worldwide with full documentation.',
      technicalSupport: 'Technical Support',
      technicalDesc: 'Expert guidance on selection, processing, and application for your specific requirements.',
      sustainablePractice: 'Sustainable Practices',
      sustainableDesc: 'Environmentally responsible quarrying with restoration programs and minimal waste.',
      seoContent: {
        h1: 'Premium Natural Quartzite Blocks with Wood Grain Patterns',
        intro: 'Discover our exclusive collection of natural quartzite blocks featuring stunning wood grain patterns. Sourced directly from our Raisen mine in India, these premium stone blocks offer the perfect combination of natural beauty, exceptional durability, and versatile applications for luxury architectural projects worldwide.'
      }
    },
    zh: {
      pageTitle: '荒料',
      subtitle: '优质石英岩荒料',
      description: '直接来自我们的Raisen采石场，我们提供最优质的天然石英岩荒料，拥有令人惊叹的木纹图案。每块荒料都经过精心开采和检验，以满足豪华建筑项目的最高国际标准。',
      overview: '产品概述',
      specifications: '技术规格',
      applications: '应用领域',
      features: '主要特点',
      mine: '矿山位置',
      shipping: '装运港口',
      availability: '库存状态',
      inStock: '现货 - 可立即发货',
      contactForPrice: '联系询价',
      requestQuote: '索取报价',
      viewDetails: '查看详情',
      whyChoose: '为什么选择我们的石英岩荒料？',
      directSource: '源头直供',
      directSourceDesc: '直接从采石场供货，无中间商，确保最优价格和稳定品质。',
      qualityAssurance: '质量保证',
      qualityDesc: '每块荒料都经过检验和分级，符合国际高端项目标准。',
      globalShipping: '全球运输',
      globalDesc: '从厦门港高效物流至全球各地，提供完整单证。',
      technicalSupport: '技术支持',
      technicalDesc: '为您的特定需求提供选材、加工和应用方面的专业指导。',
      sustainablePractice: '可持续发展',
      sustainableDesc: '环保开采，配套修复计划，废料最小化。',
      seoContent: {
        h1: '优质天然木纹石英岩荒料',
        intro: '探索我们独家的天然石英岩荒料系列，展现迷人的木纹图案。这些优质石材荒料直接来自印度Raisen矿山，为全球奢华建筑项目提供自然美感、卓越耐久性和多样化应用的完美结合。'
      }
    },
    it: {
      pageTitle: 'BLOCCHI',
      subtitle: 'Blocchi di Quarzite Premium',
      description: 'Direttamente dalla nostra cava di Raisen, offriamo la migliore selezione di blocchi di quarzite naturale con splendidi motivi a venature di legno. Ogni blocco è accuratamente estratto e ispezionato per soddisfare i più alti standard internazionali per progetti architettonici di lusso.',
      overview: 'Panoramica Prodotto',
      specifications: 'Specifiche Tecniche',
      applications: 'Applicazioni',
      features: 'Caratteristiche Principali',
      mine: 'Ubicazione Miniera',
      shipping: 'Porto di Spedizione',
      availability: 'Disponibilità',
      inStock: 'Disponibile - Pronto per la Spedizione',
      contactForPrice: 'Contatta per Prezzi',
      requestQuote: 'Richiedi Preventivo',
      viewDetails: 'Vedi Dettagli Completi',
      whyChoose: 'Perché Scegliere i Nostri Blocchi di Quarzite?',
      directSource: 'Fonte Diretta',
      directSourceDesc: 'Elimina gli intermediari con accesso diretto alla cava, garantendo i migliori prezzi e qualità costante.',
      qualityAssurance: 'Garanzia di Qualità',
      qualityDesc: 'Ogni blocco ispezionato e classificato per soddisfare gli standard internazionali per progetti premium.',
      globalShipping: 'Spedizione Globale',
      globalDesc: 'Logistica efficiente dal porto di Xiamen verso destinazioni in tutto il mondo con documentazione completa.',
      technicalSupport: 'Supporto Tecnico',
      technicalDesc: 'Guida esperta su selezione, lavorazione e applicazione per le vostre esigenze specifiche.',
      sustainablePractice: 'Pratiche Sostenibili',
      sustainableDesc: 'Estrazione responsabile con programmi di ripristino e minimo spreco.',
      seoContent: {
        h1: 'Blocchi di Quarzite Naturale Premium con Venature Legno',
        intro: 'Scopri la nostra collezione esclusiva di blocchi di quarzite naturale con splendide venature effetto legno. Provenienti direttamente dalla nostra miniera di Raisen in India, questi blocchi di pietra premium offrono la combinazione perfetta di bellezza naturale, durabilità eccezionale e applicazioni versatili per progetti architettonici di lusso in tutto il mondo.'
      }
    }
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    setIsLoaded(true);

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
    };
  }, []);


  const handleContactClick = () => {
    setCurrentPage('contact');
  };

  return (
    <div className={`blocks-container ${isLoaded ? 'loaded' : ''}`}>
      {/* Hero Section - Similar to Contact Page */}
      <section className="blocks-hero">
        <div className="hero-background">
          <div className="parallax-layer layer-1" />
        </div>
        <div className="hero-content1">
          <h1 className="hero-title">{t.pageTitle}</h1>
          <p className="hero-subtitle">{t.subtitle}</p>
          <p className="hero-description">{t.description}</p>
        </div>
      </section>


      {/* Products Grid */}
      <section className="products-section" ref={productsRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">OUR COLLECTION</span>
            <h2 className="section-title1">{t.seoContent.h1}</h2>
            <p className="section-subtitle">{t.seoContent.intro}</p>
          </div>

          <div className="products-grid">
            {products.map((product, index) => (
              <article 
                key={product.id} 
                className="product-card animate-on-scroll"
                
                onClick={() => setSelectedProduct(index)}
              >
                <div className="product-image-wrapper">
                  <img src={product.image} alt={`${product.name} - Natural Quartzite Block from ${product.mine} Mine`} />
                  <div className="product-overlay">
                    <button className="view-btn">{t.viewDetails}</button>
                  </div>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-meta">
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/>
                      </svg>
                      {t.mine}: {product.mine}
                    </span>
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M21 16.5c0 .38-.21.71-.53.88l-7 4c-.15.08-.31.12-.47.12s-.32-.04-.47-.12l-7-4A1 1 0 015 16.5v-9c0-.38.21-.71.53-.88l7-4c.3-.16.64-.16.94 0l7 4c.32.17.53.5.53.88v9z" fill="currentColor"/>
                      </svg>
                      {t.shipping}: {product.port}
                    </span>
                  </div>
                  <div className="product-availability">
                    <span className="availability-badge">{t.inStock}</span>
                  </div>
                  <button className="contact-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleContactClick();
                  }}>
                    {t.contactForPrice}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section animate-on-scroll">
        <div className="section-container">
          <h2 className="section-title">{t.whyChoose}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.directSource}</h3>
              <p>{t.directSourceDesc}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.qualityAssurance}</h3>
              <p>{t.qualityDesc}</p>
            </div>
            <div className="benefit-card">
                <div className="benefit-icon">
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                    <path d="M3 3h13v13H3V3zm14 0h4l2 4v9h-6V3zm-1 14c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zm-10 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zM5 5v9h9V5H5zm14 4l-1.5-3H17v3h2z"/>
                    </svg>
                    </div>
                    <h3>{t.globalShipping}</h3>
                    <p>{t.globalDesc}</p>
                </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.sustainablePractice}</h3>
              <p>{t.sustainableDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct !== null && (
        <div className="product-modal" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProduct(null)}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="modal-grid">
              <div className="modal-image">
                <img src={products[selectedProduct].image} alt={products[selectedProduct].name} />
              </div>

              <div className="modal-details">
                <h2 className="modal-title">{products[selectedProduct].name}</h2>
                <p className="modal-description">{products[selectedProduct].description}</p>

                <div className="modal-tabs">
                  <button 
                    className={`tab-btn ${activeTab === 'specifications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('specifications')}
                  >
                    {t.specifications}
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
                    onClick={() => setActiveTab('features')}
                  >
                    {t.features}
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('applications')}
                  >
                    {t.applications}
                  </button>
                </div>

                <div className="tab-content">
                  {activeTab === 'specifications' && (
                    <div className="specs-list">
                      {Object.entries(products[selectedProduct].technicalSpecs).map(([key, value]) => (
                        <div key={key} className="spec-item">
                          <span className="spec-key">{key}:</span>
                          <span className="spec-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <ul className="features-list">
                      {products[selectedProduct].features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  )}

                  {activeTab === 'applications' && (
                    <ul className="applications-list">
                      {products[selectedProduct].applications.map((application, index) => (
                        <li key={index}>{application}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="primary-btn" onClick={handleContactClick}>{t.requestQuote}</button>
                  <div className="shipping-info">
                    <p><strong>{t.mine}:</strong> {products[selectedProduct].mine}</p>
                    <p><strong>{t.shipping}:</strong> {products[selectedProduct].port}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEO Content Section */}
      <section className="seo-content-section">
        <div className="section-container">
          <div className="seo-content">
            <h3>Natural Quartzite Blocks - The Ultimate Wood Grain Stone Solution</h3>
            <p>
              Our premium quartzite blocks represent a revolutionary advancement in natural stone technology. 
              Each block, whether it's our Norwegian Wood Grain, Brazilian Wood Grain, Teak Wood Quartzite, 
              or classic Quartzite Borghese, is carefully extracted from our exclusive Raisen mine in India, 
              ensuring consistent quality and availability for large-scale projects worldwide.
            </p>
            
            <h3>Why Wood Grain Quartzite is Superior to Traditional Materials</h3>
            <p>
              Wood grain quartzite combines the aesthetic appeal of natural wood with the unmatched durability 
              of quartzite stone. Unlike traditional wood, our quartzite blocks require no maintenance, resist 
              all weather conditions, and maintain their beauty for centuries. The natural patterns formed over 
              millions of years create unique, unrepeatable designs that add value to any architectural project.
            </p>

            <h3>Direct from Raisen Mine to Your Project</h3>
            <p>
              As the direct source from Raisen mine, we offer competitive pricing and guaranteed supply for 
              projects of any scale. Our strategic shipping location at Xiamen port ensures efficient global 
              distribution with complete documentation for smooth customs clearance. Whether you're searching 
              for Norwegian Wood Grain quartzite for a Scandinavian-inspired project or Brazilian Wood Grain 
              for tropical architecture, we provide comprehensive support from selection to delivery.
            </p>

            <h3>Technical Excellence Meets Natural Beauty</h3>
            <p>
              With compressive strength exceeding 98 MPa and water absorption below 1.6%, our quartzite blocks 
              surpass international standards for commercial and residential applications. The high silica content 
              (96%+) ensures exceptional resistance to acids, stains, and environmental factors, making these 
              blocks ideal for both interior luxury applications and exterior architectural elements.
            </p>
          </div>
        </div>
      </section>

      {/* Floating Background Elements */}
      <div className="background-shapes">
        <div 
          className="shape shape-1"
          style={{ transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)` }}
        />
        <div 
          className="shape shape-2"
          style={{ transform: `translate(${mousePosition.x * -15}px, ${mousePosition.y * -15}px)` }}
        />
      </div>
    </div>
  );
};

export default Blocks;